import React, { useState, createElement } from 'react';
import { Download, Info, ChevronDown, Calendar, Filter, TrendingUp, DollarSign, Brain, MessageCircle, Gift, Clock, AlertCircle, Droplet, Fish, Wallet, Heart } from 'lucide-react';
// Sample data for segments
const segments = [{
  id: 'whales',
  label: 'Whales',
  icon: <DollarSign size={16} className="text-purple-500" data-id="element-10858" />,
  color: 'purple'
}, {
  id: 'dolphins',
  label: 'Dolphins',
  icon: <Droplet size={16} className="text-blue-500" data-id="element-10859" />,
  color: 'blue'
}, {
  id: 'minnows',
  label: 'Minnows',
  icon: <Fish size={16} className="text-cyan-500" data-id="element-10860" />,
  color: 'cyan'
}, {
  id: 'non-spenders',
  label: 'Non-Spenders',
  icon: <Wallet size={16} className="text-amber-500" data-id="element-10861" />,
  color: 'amber'
}, {
  id: 'churn-risks',
  label: 'Churn Risks',
  icon: <AlertCircle size={16} className="text-red-500" data-id="element-10862" />,
  color: 'red'
}, {
  id: 'emotals',
  label: 'Emotals',
  icon: <Heart size={16} className="text-pink-500" data-id="element-10863" />,
  color: 'pink'
}];
// Sample ritual types
const ritualTypes = [{
  id: 'daily-check-in',
  label: 'Daily Check-in',
  icon: '📅',
  color: 'bg-indigo-100 dark:bg-indigo-900/30',
  textColor: 'text-indigo-500'
}, {
  id: 'daily-chat',
  label: 'Daily Chat',
  icon: '💬',
  color: 'bg-green-100 dark:bg-green-900/30',
  textColor: 'text-green-500'
}, {
  id: 'special-events',
  label: 'Special Events',
  icon: '✨',
  color: 'bg-amber-100 dark:bg-amber-900/30',
  textColor: 'text-amber-500'
}, {
  id: 'weekly-challenge',
  label: 'Weekly Challenge',
  icon: '🏆',
  color: 'bg-red-100 dark:bg-red-900/30',
  textColor: 'text-red-500'
}];
// Sample data for ritual performance
const ritualPerformance = [{
  segment: 'whales',
  rituals: {
    'daily-check-in': {
      completion: 82,
      monetization: 12.75,
      audience: 'Lookalike Audiences'
    },
    'daily-chat': {
      completion: 92,
      monetization: 28.75,
      audience: 'Lookalike Audiences'
    },
    'special-events': {
      completion: 78,
      monetization: 45.5,
      audience: 'Lookalike Audiences'
    },
    'weekly-challenge': {
      completion: 68,
      monetization: 38.25,
      audience: 'Lookalike Audiences'
    }
  }
}, {
  segment: 'dolphins',
  rituals: {
    'daily-check-in': {
      completion: 72,
      monetization: 8.25,
      audience: 'Lookalike Audiences'
    },
    'daily-chat': {
      completion: 83,
      monetization: 15.5,
      audience: 'Lookalike Audiences'
    },
    'special-events': {
      completion: 63,
      monetization: 32.75,
      audience: 'Lookalike Audiences'
    },
    'weekly-challenge': {
      completion: 54,
      monetization: 25.2,
      audience: 'Lookalike Audiences'
    }
  }
}, {
  segment: 'minnows',
  rituals: {
    'daily-check-in': {
      completion: 58,
      monetization: 4.5,
      audience: 'Broad Targeting'
    },
    'daily-chat': {
      completion: 68,
      monetization: 7.25,
      audience: 'Broad Targeting'
    },
    'special-events': {
      completion: 45,
      monetization: 18.5,
      audience: 'Broad Targeting'
    },
    'weekly-challenge': {
      completion: 38,
      monetization: 12.3,
      audience: 'Broad Targeting'
    }
  }
}, {
  segment: 'non-spenders',
  rituals: {
    'daily-check-in': {
      completion: 32,
      monetization: 0.75,
      audience: 'Context Fallback'
    },
    'daily-chat': {
      completion: 42,
      monetization: 1.25,
      audience: 'Context Fallback'
    },
    'special-events': {
      completion: 28,
      monetization: 2.75,
      audience: 'Context Fallback'
    },
    'weekly-challenge': {
      completion: 15,
      monetization: 1.5,
      audience: 'Context Fallback'
    }
  }
}, {
  segment: 'churn-risks',
  rituals: {
    'daily-check-in': {
      completion: 25,
      monetization: 3.25,
      audience: 'Persona Fallback'
    },
    'daily-chat': {
      completion: 35,
      monetization: 5.75,
      audience: 'Persona Fallback'
    },
    'special-events': {
      completion: 32,
      monetization: 12.25,
      audience: 'Persona Fallback'
    },
    'weekly-challenge': {
      completion: 12,
      monetization: 8.4,
      audience: 'Persona Fallback'
    }
  }
}];
export const EngagementRituals: React.FC = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [exportSuccess, setExportSuccess] = useState(false);
  const handleExportData = () => {
    // Prepare the data for export
    const exportData = [];
    // Add header row
    exportData.push(['Segment', 'Ritual Type', 'Completion Rate', 'Monetization', 'Audience Type', 'Growth vs Last Month']);
    // Add data rows
    ritualPerformance.forEach(segmentData => {
      const segment = segments.find(s => s.id === segmentData.segment);
      Object.entries(segmentData.rituals).forEach(([ritualId, ritualData]) => {
        const ritual = ritualTypes.find(r => r.id === ritualId);
        exportData.push([segment.label, ritual.label, `${ritualData.completion}%`, `$${ritualData.monetization.toFixed(2)}`, ritualData.audience, `+${(Math.random() * 10).toFixed(1)}%`]);
      });
    });
    // Convert to CSV
    const csvContent = exportData.map(row => row.map(cell => typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell).join(',')).join('\n');
    // Create a blob and download link
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'engagement_rituals_data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Show success message
    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 3000);
  };
  // Calculate overall metrics
  const avgCompletionRate = ritualPerformance.flatMap(segment => Object.values(segment.rituals).map(ritual => ritual.completion)).reduce((sum, rate) => sum + rate, 0) / (ritualPerformance.length * Object.keys(ritualPerformance[0].rituals).length);
  const avgMonetization = ritualPerformance.flatMap(segment => Object.values(segment.rituals).map(ritual => ritual.monetization)).reduce((sum, value) => sum + value, 0) / (ritualPerformance.length * Object.keys(ritualPerformance[0].rituals).length);
  const algorithmCoverage = 72.3; // Sample value
  return <div className="w-full" data-id="element-10864">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4" data-id="element-10865">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-10866">
          Engagement Rituals
        </h2>
        <div className="flex items-center gap-3" data-id="element-10867">
          <div className="flex items-center" data-id="element-10868">
            <select className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md text-sm" value={selectedSegment} onChange={e => setSelectedSegment(e.target.value)} data-id="element-10869">
              <option value="all" data-id="element-10870">All Users</option>
              {segments.map(segment => <option key={segment.id} value={segment.id} data-id="element-10871">
                  {segment.label}
                </option>)}
            </select>
            <ChevronDown className="w-4 h-4 -ml-6 text-gray-500 pointer-events-none" data-id="element-10872" />
          </div>
          <button onClick={handleExportData} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-10873">
            <Download size={16} data-id="element-10874" />
            <span data-id="element-10875">Export Data</span>
          </button>
          {exportSuccess && <div className="absolute top-4 right-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-4 py-2 rounded-md shadow-md" data-id="element-10876">
              Data exported successfully!
            </div>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-10877">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4" data-id="element-10878">
          <div className="flex justify-between items-center" data-id="element-10879">
            <div data-id="element-10880">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-10881">
                Avg. Completion Rate
              </p>
              <div className="flex items-baseline mt-1" data-id="element-10882">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-10883">
                  {avgCompletionRate.toFixed(1)}%
                </h3>
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center" data-id="element-10884">
              <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-10885" />
            </div>
          </div>
          <div className="flex items-center mt-2" data-id="element-10886">
            <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center" data-id="element-10887">
              <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-10888" />
              +5.2% vs last month
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4" data-id="element-10889">
          <div className="flex justify-between items-center" data-id="element-10890">
            <div data-id="element-10891">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-10892">
                Avg. Monetization
              </p>
              <div className="flex items-baseline mt-1" data-id="element-10893">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-10894">
                  ${avgMonetization.toFixed(2)}
                </h3>
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-10895">
              <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-10896" />
            </div>
          </div>
          <div className="flex items-center mt-2" data-id="element-10897">
            <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center" data-id="element-10898">
              <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-10899" />
              +8.7% vs last month
            </span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4" data-id="element-10900">
          <div className="flex justify-between items-center" data-id="element-10901">
            <div data-id="element-10902">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-10903">
                Algorithm Coverage
              </p>
              <div className="flex items-baseline mt-1" data-id="element-10904">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-10905">
                  {algorithmCoverage.toFixed(1)}%
                </h3>
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-10906">
              <Brain className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-10907" />
            </div>
          </div>
          <div className="flex items-center mt-2" data-id="element-10908">
            <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center" data-id="element-10909">
              <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-10910" />
              +4.5% vs last month
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-10911">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4" data-id="element-10912">
          <div className="flex items-center justify-between mb-4" data-id="element-10913">
            <h3 className="text-md font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10914">
              <Calendar className="w-4 h-4 text-amber-500 mr-2" data-id="element-10915" />
              Completion Rate by Segment
            </h3>
            <div className="flex items-center gap-2" data-id="element-10916">
              <button className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded" data-id="element-10917">
                Weekly
              </button>
              <button className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded" data-id="element-10918">
                Monthly
              </button>
            </div>
          </div>
          {/* This would be a chart in a real implementation */}
          <div className="h-64 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-center" data-id="element-10919">
            <span className="text-gray-500 dark:text-gray-400 text-sm" data-id="element-10920">
              Completion Rate Chart
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-3" data-id="element-10921">
            {ritualTypes.map(ritual => <div key={ritual.id} className="flex items-center" data-id="element-10922">
                <div className={`w-3 h-3 rounded-full bg-${ritual.color.split('-')[1]}-500 mr-1`} data-id="element-10923"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-10924">
                  {ritual.label}
                </span>
              </div>)}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4" data-id="element-10925">
          <div className="flex items-center justify-between mb-4" data-id="element-10926">
            <h3 className="text-md font-medium text-gray-900 dark:text-white flex items-center" data-id="element-10927">
              <DollarSign className="w-4 h-4 text-green-500 mr-2" data-id="element-10928" />
              Monetization by Segment
            </h3>
            <div className="flex items-center gap-2" data-id="element-10929">
              <button className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded" data-id="element-10930">
                Weekly
              </button>
              <button className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded" data-id="element-10931">
                Monthly
              </button>
            </div>
          </div>
          {/* This would be a chart in a real implementation */}
          <div className="h-64 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-center" data-id="element-10932">
            <span className="text-gray-500 dark:text-gray-400 text-sm" data-id="element-10933">
              Monetization Chart
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-3" data-id="element-10934">
            {ritualTypes.map(ritual => <div key={ritual.id} className="flex items-center" data-id="element-10935">
                <div className={`w-3 h-3 rounded-full bg-${ritual.color.split('-')[1]}-500 mr-1`} data-id="element-10936"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-10937">
                  {ritual.label}
                </span>
              </div>)}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6" data-id="element-10938">
        <div className="flex items-center justify-between mb-4" data-id="element-10939">
          <h3 className="text-md font-medium text-gray-900 dark:text-white" data-id="element-10940">
            Ritual Performance by Segment
          </h3>
          <button className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded flex items-center" data-id="element-10941">
            <Filter className="w-3 h-3 mr-1" data-id="element-10942" />
            Filter
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-10943">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-10944">
            <thead data-id="element-10945">
              <tr className="bg-gray-50 dark:bg-gray-800/60" data-id="element-10946">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10947">
                  Ritual
                </th>
                {segments.map(segment => <th key={segment.id} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-10948">
                    <div className="flex items-center" data-id="element-10949">
                      {segment.icon}
                      <span className="ml-1" data-id="element-10950">{segment.label}</span>
                    </div>
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-10951">
              {ritualTypes.map(ritual => <tr key={ritual.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30" data-id="element-10952">
                  <td className="px-4 py-3 whitespace-nowrap" data-id="element-10953">
                    <div className="flex items-center" data-id="element-10954">
                      <div className={`w-8 h-8 rounded-md ${ritual.color} flex items-center justify-center mr-2`} data-id="element-10955">
                        <span data-id="element-10956">{ritual.icon}</span>
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-10957">
                        {ritual.label}
                      </span>
                    </div>
                  </td>
                  {segments.map(segment => {
                const segmentData = ritualPerformance.find(s => s.segment === segment.id);
                const ritualData = segmentData?.rituals[ritual.id];
                return <td key={segment.id} className="px-4 py-3 whitespace-nowrap" data-id="element-10958">
                        {ritualData && <div data-id="element-10959">
                            <div className="flex items-center justify-between mb-1" data-id="element-10960">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-10961">
                                Comp: {ritualData.completion}%
                              </span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-10962">
                                ${ritualData.monetization.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-10963">
                              <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" data-id="element-10964"></span>
                              <span data-id="element-10965">
                                {Math.floor(Math.random() * 10) + 70}%
                              </span>
                            </div>
                            <div className="mt-1" data-id="element-10966">
                              <select className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800" data-id="element-10967">
                                <option data-id="element-10968">{ritualData.audience}</option>
                              </select>
                            </div>
                          </div>}
                      </td>;
              })}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};